/**
 * Tax Master Service — Admin API integration for Tax Configuration (BMF-139).
 *
 * Backend routes (all admin-only, under the global /api prefix):
 *   GET    /tax-master            list / search (page, limit, search, taxType, isActive)
 *   GET    /tax-master/:id        get one by id
 *   POST   /tax-master           create (CGST/SGST/IGST derived from GST server-side)
 *   PATCH  /tax-master/:id        update (re-derives CGST/SGST/IGST when GST changes)
 *   PATCH  /tax-master/:id/active activate / deactivate
 *
 * The service derives CGST/SGST/IGST from gstPercentage, so those are never sent
 * from the client — only { taxCode, taxType, description, gstPercentage, isActive }.
 */

import api from './api';

const TAX_MASTER_URL = '/tax-master';

const taxMasterService = {
  /**
   * List tax configurations with optional search / filters + paging.
   * @param {Object} params - { page, limit, search, taxType, gstPercentage, isActive }
   *   taxType: 'GOODS' | 'SERVICE'; isActive: 'true' | 'false'
   * @returns {Promise<{ data: Array, pagination: Object }>}
   */
  list: async (params = {}) => {
    const query = new URLSearchParams();

    // Send a param only when the caller actually provided a value. The intent
    // differs by type, so the guard is explicit rather than a blanket truthiness
    // check (which would, e.g., drop a legitimate page=0):
    //   - numeric params: send whenever defined (an explicit 0 is still a value).
    //   - string filters: send only a non-empty value — an empty string is a
    //     "no filter" sentinel (the cleared dropdown / empty search box) and must
    //     never be sent as a blank ?param= that the backend would ignore anyway.
    const appendNum = (key, v) => { if (v !== undefined && v !== null) query.append(key, String(v)); };
    const appendStr = (key, v) => { if (v !== undefined && v !== null && v !== '') query.append(key, String(v)); };

    appendNum('page', params.page);
    appendNum('limit', params.limit);
    appendStr('search', params.search);
    appendStr('taxType', params.taxType);
    appendStr('gstPercentage', params.gstPercentage);
    appendStr('isActive', params.isActive);

    const qs = query.toString();
    const response = await api.get(qs ? `${TAX_MASTER_URL}?${qs}` : TAX_MASTER_URL);
    return response.data;
  },

  /** Get a single tax configuration by id. */
  getById: async (id) => {
    const response = await api.get(`${TAX_MASTER_URL}/${id}`);
    return response.data;
  },

  /**
   * Create a tax configuration (admin only).
   * @param {Object} data - { taxCode, taxType, description, gstPercentage, isActive }
   */
  create: async (data) => {
    const response = await api.post(TAX_MASTER_URL, {
      taxCode: (data.taxCode ?? '').trim(),
      taxType: data.taxType,
      // On CREATE, a blank description is omitted (undefined) — there is no
      // existing value to clear, so the backend simply defaults it to null.
      // (UPDATE intentionally differs: it sends '' to actively clear an existing
      // description — see the note there.)
      description: data.description?.trim() || undefined,
      gstPercentage: Number(data.gstPercentage),
      ...(data.isActive !== undefined ? { isActive: data.isActive } : {}),
    });
    return response.data;
  },

  /**
   * Update a tax configuration (admin only). Only defined fields are sent.
   */
  update: async (id, updates) => {
    const payload = {};
    if (updates.taxCode !== undefined) payload.taxCode = updates.taxCode.trim();
    if (updates.taxType !== undefined) payload.taxType = updates.taxType;
    // On UPDATE, a provided-but-blank description sends '' (NOT undefined) so the
    // backend actively clears it (it maps '' → null). Using undefined here would
    // be dropped from the JSON body, and the backend — which only touches the
    // column when the key is present — would keep the old description, silently
    // defeating a user who blanked the field. The key is still gated on
    // `!== undefined` so an update that doesn't mention description leaves it be.
    if (updates.description !== undefined) payload.description = updates.description?.trim() ?? '';
    if (updates.gstPercentage !== undefined) payload.gstPercentage = Number(updates.gstPercentage);
    if (updates.isActive !== undefined) payload.isActive = updates.isActive;
    const response = await api.patch(`${TAX_MASTER_URL}/${id}`, payload);
    return response.data;
  },

  /**
   * Activate / deactivate a tax configuration (admin only).
   * Omit `isActive` to flip the current value.
   */
  setActive: async (id, isActive) => {
    const body = isActive === undefined ? {} : { isActive };
    const response = await api.patch(`${TAX_MASTER_URL}/${id}/active`, body);
    return response.data;
  },
};

export default taxMasterService;
